import React from "react";
import { RenderMediaContent } from "src/components/RenderMediaContent";
import deckshortcuts from "../../assets/setup/deckshortcuts.png";

// full list of flags
const FLAGS = [
  { flag: "--alwaysOnTop", desc: "Keeps Deck Hopper always on top." },
  { flag: "--fullscreen", desc: "Automatically enters full‑screen mode when Deck Hopper connects to SAMMI." },
  { flag: "--autoConnect", desc: "Automatically connects to SAMMI." },
  { flag: "--clientName=YourClientName", desc: "Sets the client name for SAMMI Deck App commands." },
  { flag: "--port=9470", desc: "Specifies the port to connect to." },
  { flag: "--ip=192.168.1.67", desc: "Specifies the IP address to connect to." },
  { flag: "--password=1234", desc: "Specifies the password for the connection." },
  { flag: "--deckId=YOURDECKID", desc: "Specifies the deck ID to enter automatically." },
];

export const LaunchFlags: React.FC = () => (
  <div className="flex flex-col gap-y-4 mx-auto px-4 max-w-screen-sm">
    {/* intro */}
    <p>
      This Pro feature lets you start Deck Hopper with flags—e.g., jump straight
      into a deck. Desktop only.
    </p>

    {/* steps */}
    <ol className="list-decimal list-inside space-y-2">
      <li>
        Locate <b>Deck Hopper.exe</b>, right‑click it, then <b>Create shortcut</b>.
      </li>
      <li>
        In <b>SAMMI Core</b> right‑click the deck → <b>Copy ID</b>.
      </li>
      <li>
        Right‑click your shortcut → <b>Properties</b>. Append{" "}
        <code>--deckId=&lt;YOURDECKID&gt;</code> at the end of the <b>Target</b>{" "}
        line. Example:
        <code className="block whitespace-pre-wrap break-all">
          "C:\Path\To\Deck Hopper.exe" --deckId=12345
        </code>
      </li>
      <li>
        Click <b>Apply</b> then <b>OK</b>.
      </li>
      <li>
        <b>Multiple decks?</b> Make extra shortcuts, each with its own <code>--deckId</code>.
      </li>
    </ol>

    {/* flags – list for ≤ sm, table for ≥ md */}
    <section>
      <h2 className="text-lg font-semibold mt-4">All supported flags</h2>

      {/* mobile list */}
      <dl className="block md:hidden mt-2 space-y-3">
        {FLAGS.map(({ flag, desc }) => (
          <div key={flag} className="break-words">
            <dt className="font-medium">{flag}</dt>
            <dd className="ml-4 text-gray-200">{desc}</dd>
          </div>
        ))}
      </dl>

      {/* desktop / tablet table */}
      <div className="hidden md:block overflow-x-auto mt-2">
        <table className="table-fixed w-full text-left">
          <colgroup>
            <col className="w-1/3" />
            <col className="w-2/3" />
          </colgroup>
          <thead className="text-xs font-semibold uppercase text-white-400 bg-green-600/50">
            <tr>
              <th className="p-2">Flag</th>
              <th className="p-2">Description</th>
            </tr>
          </thead>
          <tbody className="text-sm divide-y divide-gray-100">
            {FLAGS.map(({ flag, desc }) => (
              <tr key={flag}>
                <td className="p-2 break-words">{flag}</td>
                <td className="p-2 break-words">{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* multi‑flag example */}
    <div>
      <strong>Example with multiple flags:</strong>
      <pre className="bg-gray-700 p-2 rounded mt-1 overflow-x-auto text-sm whitespace-pre-wrap break-all">
        <code>
          "Deck Hopper.exe" --alwaysOnTop --fullscreen --autoConnect
          --clientName=DeckHopper --port=9470 --ip=192.168.1.4 --password=1234
          --deckId=20240229011334564759505
        </code>
      </pre>
    </div>

    {/* image */}
    <div className="overflow-x-auto">
      <RenderMediaContent src={deckshortcuts} />
    </div>
  </div>
);
